import { useNavigate } from "react-router-dom";
import { usePedidos } from "../../hooks/usePedidos";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useEffect, useMemo, useState } from "react";
import { IPedido } from "../../interface/IPedido";
import { IPagamentoPedido } from "../../interface/IPagamentoPedido";
import { Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Link, MenuItem, Select, Skeleton, Switch, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Container } from "./styles";
import { useDataHora } from "../../../core/hooks/useDataHora";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { TipoPedido } from "../../enum/TipoPedido";
import { MetodoPagamento } from "../../../core/enum/MetodoPagamento";
import { TipoCartao } from "../../../core/enum/TipoCartao";
import { useFetchMaquininhas } from "../../hooks/useFetchMaquininhas";
import { useFetchBandeirasCartao } from "../../hooks/useFetchBandeirasCartao";
import HistoricoPedidoTimeline from "../../components/HistoricoPedidoTimeline";
import { TipoAnexoDescricao, TipoAnexoPedido } from "../../enum/TipoAnexoPedido";
import { StatusAnexoPedido } from "../../enum/StatusAnexoPedido";
import { Download, Place, OpenInNew, Print, Info, Add, ReceiptLong, LocalShipping, CreditCard, Receipt, ConfirmationNumber, Restaurant, Person, AttachFile, History, StarRounded } from '@mui/icons-material';
import { useDownload } from "../../../core/hooks/useDownload";
import { useConfiguracoesPedidoContext } from "../../contexts/ConfiguracoesPedidoContext";
import { IDadosEmitirNotaFiscal } from "../../interface/IDadosEmitirNotaFiscal";
import { EmitirNotaFiscalModal } from "../../pages/Operacao/containers/EmitirNotaFiscalModal";
import { EmitirNFeModal } from "../../pages/Operacao/containers/EmitirNFeModal";
import { IDadosEmitirNFe } from "../../interface/IDadosEmitirNFe";
import { useFetchMarketplaces } from "../../../marketplace/hooks/useFetchMarketplaces";
import { IMarketplace } from "../../../marketplace/interfaces/IMarketplace";
import { TipoMarketplace } from "../../../marketplace/enum/TipoMarketplace";
import { TipoCupom, TipoCupomDescricao } from "../../enum/TipoCupom";
import { StatusPedido } from "../../enum/StatusPedido";
import { ImprimirModal } from "../../components/ImprimirModal";
import { useWebSocketPedidosContext } from "../../contexts/WebSocketPedidosContext";
import { PatrocinadoresCupomModal } from "../../components/PatrocinadoresCupomModal";
import { IPatrocinadorCupom } from "../../interface/IPatrocinadorCupom";
import { calcularValoresComCupons } from "../../utils/calcularValoresComCupons";
import { calcularDescontoCupomComCuponsSelecionados } from "../../utils/calcularDescontoCupomComCuponsSelecionados";
import { AtualizarStatusPedido } from "../../components/AtualizarStatusPedido";
import { useLarguraElemento } from "../../../core/hooks/useLarguraElemento";
import { CelulaCabecalho, CelulaCorpo, LinhaTabela } from "../../../core/styles/superficies";
import { CabecalhoPedido } from "./components/CabecalhoPedido";
import { CartaoPagamento } from "./components/CartaoPagamento";
import { ParDado } from "./components/ParDado";
import { ResumoFinanceiro } from "./components/ResumoFinanceiro";
import { SecaoCartao } from "./components/SecaoCartao";
import { AvaliacaoPedidoDetalhe } from "../../components/AvaliacaoPedido";
import { GrupoProdutoPedido, SecaoProdutos } from "./components/SecaoProdutos";

/** Intervalo do fallback de atualização enquanto um anexo aguarda a SEFAZ. */
const INTERVALO_ATUALIZACAO_ANEXO_MS = 10000;

/**
 * Abaixo desta largura a tela passa a uma coluna. É a largura do container, não da janela:
 * a mesma tela aparece num drawer estreito e ocupando a página inteira.
 */
const LARGURA_COMPACTA = 720;

/**
 * Cor de realce do ícone de cada seção. Com oito cartões visíveis ao mesmo tempo, a cor é o
 * que permite reencontrar uma seção sem reler todos os títulos. Produtos e Pagamentos ficam
 * sem entrada porque usam a cor primária do tema — são as duas seções principais da tela.
 */
const CORES_SECAO = {
    valores: "#2e7d32",
    cliente: "#0288d1",
    entrega: "#ed6c02",
    pagamentos: undefined,
    cupons: "#7b1fa2",
    anexos: "#0288d1",
    timeline: "#5e6a75",
    avaliacao: "#f9a825",
} as const;

interface PedidoDetalhadoProps {
    uuid: string;
    onStatusAtualizado?: () => void;
    dialogZIndex?: number;
}

interface FormularioPagamento {
    metodoPagamento: string;
    valor: number;
    pago: boolean;
    valorRecebido?: number;
    tipoCartao?: string;
    maquininhaUuid?: string;
    maquininhaNome?: string;
    bandeiraUuid?: string;
    bandeiraNome?: string;
    numeroAutorizacao?: string;
}

const PAGAMENTO_VAZIO: FormularioPagamento = { metodoPagamento: '', valor: 0, pago: false };

/**
 * O PIX integrado grava a instituição ("Woovi") sem uuid, porque ela não é uma maquininha
 * cadastrada. Este valor mantém o nome visível no select sem que salvar o formulário o apague.
 */
const MAQUININHA_INTEGRACAO = '__integracao__';

export const PedidoDetalhado = ({ uuid, onStatusAtualizado, dialogZIndex }: PedidoDetalhadoProps) => {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { buscarPedido, gerarAnexo, emitirNotaFiscal, emitirNFe, loading, loadingAnexo, atualizarPagamentos } = usePedidos();

    const [refContainer, larguraContainer] = useLarguraElemento<HTMLDivElement>();
    // Enquanto a largura é 0 ainda não houve medição; assumir o layout largo evita um salto
    // visível de uma coluna para duas no primeiro render.
    const compacto = larguraContainer > 0 && larguraContainer < LARGURA_COMPACTA;

    const [pedido, setPedido] = useState<undefined | IPedido>(undefined);
    const [pagamentosLocal, setPagamentosLocal] = useState<IPagamentoPedido[]>([]);
    const [modalNovoPagamentoOpen, setModalNovoPagamentoOpen] = useState(false);
    const [novoPagamento, setNovoPagamento] = useState<FormularioPagamento>(PAGAMENTO_VAZIO);
    // null = está adicionando; número = está editando o pagamento daquela posição.
    const [pagamentoEditandoIndex, setPagamentoEditandoIndex] = useState<number | null>(null);
    const novoPagamentoTroco = (novoPagamento.valorRecebido ?? 0) - novoPagamento.valor;

    const { maquininhas } = useFetchMaquininhas(true);
    const { bandeiras } = useFetchBandeirasCartao(true);

    const [imprimirOpen, setImprimirOpen] = useState<boolean>(false);

    const abrirModalImpressao = () => setImprimirOpen(true);
    const fecharModalImpressao = () => {
        setImprimirOpen(false);
    };

    const [notaFiscalOpen, setNotaFiscalOpen] = useState<boolean>(false);
    const abrirModalNotaFiscal = () => {
        setNotaFiscalOpen(true);
    }
    const fecharModalNotaFiscal = () => {
        setNotaFiscalOpen(false);
        fecharModalImpressao();
    };

    const [nfeOpen, setNfeOpen] = useState<boolean>(false);
    const abrirModalNFe = () => setNfeOpen(true);
    const fecharModalNFe = () => setNfeOpen(false);

    const [patrocinadoresModalOpen, setPatrocinadoresModalOpen] = useState<boolean>(false);
    const [patrocinadoresSelecionados, setPatrocinadoresSelecionados] = useState<IPatrocinadorCupom[]>([]);
    const abrirModalPatrocinadores = (patrocinadores: IPatrocinadorCupom[]) => {
        setPatrocinadoresSelecionados(patrocinadores);
        setPatrocinadoresModalOpen(true);
    };
    const fecharModalPatrocinadores = () => {
        setPatrocinadoresModalOpen(false);
        setPatrocinadoresSelecionados([]);
    };

    const { configuracao } = useConfiguracoesPedidoContext();

    const fluxoContexto = useMemo(() => {
        if (pedido && configuracao) {
            return configuracao
                .fluxoOperacao
                ?.fluxosContextos
                ?.find(fluxo => fluxo.tipo == pedido.tipo)
        }
    }, [pedido, configuracao])

    const { ultimoPedidoAtualizado } = useWebSocketPedidosContext();
    const handleBuscarPedido = async ({ redirecionarSeNaoEncontrado = false } = {}) => {
        if (!uuid) {
            snackbar("Pedido inválido", { severity: 'error' });
            return window.location.href = "/dashboard";
        }

        const pedidoEncontrado = await buscarPedido(uuid);
        if (!pedidoEncontrado) {
            if (redirecionarSeNaoEncontrado) {
                return window.location.href = "/dashboard";
            }
            return;
        }

        setPedido(pedidoEncontrado);
    }

    useEffect(() => {
        if (pedido?.pagamentos) {
            setPagamentosLocal([...pedido.pagamentos]);
        }
    }, [pedido?.uuid]);

    const salvarPagamentos = async (pagamentos: IPagamentoPedido[]) => {
        if (!pedido) return;
        const resultado = await atualizarPagamentos(pedido.uuid, pagamentos);
        if (!resultado) {
            snackbar("Erro ao atualizar pagamentos", { severity: "error" });
        }
    };

    const handleSetPago = (index: number, value: boolean) => {
        const updated = pagamentosLocal.map((p, i) => i === index ? { ...p, pago: value } : p);
        setPagamentosLocal(updated);
        salvarPagamentos(updated);
    };

    const handleRemoverPagamento = async (index: number) => {
        const updated = pagamentosLocal.filter((_, i) => i !== index);
        setPagamentosLocal(updated);
        await salvarPagamentos(updated);
        setTimeout(() => handleBuscarPedido(), 100);
    };

    const handleAbrirNovoPagamento = () => {
        setPagamentoEditandoIndex(null);
        setNovoPagamento(PAGAMENTO_VAZIO);
        setModalNovoPagamentoOpen(true);
    };

    const handleAbrirEdicaoPagamento = (index: number) => {
        const pagamento = pagamentosLocal[index];
        setPagamentoEditandoIndex(index);
        setNovoPagamento({
            metodoPagamento: String(pagamento.metodoPagamento ?? ''),
            valor: pagamento.valor ?? 0,
            pago: !!pagamento.pago,
            valorRecebido: pagamento.valorRecebido,
            tipoCartao: pagamento.tipoCartao,
            maquininhaUuid: pagamento.maquininhaUuid,
            maquininhaNome: pagamento.maquininhaNome,
            bandeiraUuid: pagamento.bandeiraUuid,
            bandeiraNome: pagamento.bandeiraNome,
            numeroAutorizacao: pagamento.numeroAutorizacao,
        });
        setModalNovoPagamentoOpen(true);
    };

    const handleFecharModalPagamento = () => {
        setModalNovoPagamentoOpen(false);
        setPagamentoEditandoIndex(null);
        setNovoPagamento(PAGAMENTO_VAZIO);
    };

    const handleSalvarPagamento = () => {
        if (!novoPagamento.metodoPagamento || novoPagamento.valor <= 0) return;
        const isDinheiro = novoPagamento.metodoPagamento === MetodoPagamento.Dinheiro;
        const original = pagamentoEditandoIndex !== null ? pagamentosLocal[pagamentoEditandoIndex] : undefined;

        // Todos os campos são definidos explicitamente, inclusive como undefined: ao trocar o
        // método de cartão para dinheiro, os dados de conciliação precisam ser limpos.
        const dadosConciliacao = {
            tipoCartao: isDinheiro ? undefined : novoPagamento.tipoCartao || undefined,
            maquininhaUuid: isDinheiro ? undefined : novoPagamento.maquininhaUuid || undefined,
            maquininhaNome: isDinheiro ? undefined : novoPagamento.maquininhaNome || undefined,
            bandeiraUuid: isDinheiro ? undefined : novoPagamento.bandeiraUuid || undefined,
            bandeiraNome: isDinheiro ? undefined : novoPagamento.bandeiraNome || undefined,
            numeroAutorizacao: isDinheiro ? undefined : novoPagamento.numeroAutorizacao?.trim() || undefined,
        };

        // Um dado que veio da integração e não foi alterado continua marcado como tal; se o
        // atendente mexeu nele, a origem passa a ser manual.
        const camposConciliacao = Object.keys(dadosConciliacao) as (keyof typeof dadosConciliacao)[];
        const conciliacaoAlterada = !original || camposConciliacao
            .some(campo => dadosConciliacao[campo] !== (original[campo] ?? undefined));

        const pagamento: IPagamentoPedido = {
            metodoPagamento: novoPagamento.metodoPagamento,
            valor: novoPagamento.valor,
            pago: novoPagamento.pago,
            valorRecebido: isDinheiro ? novoPagamento.valorRecebido : undefined,
            valorTroco: isDinheiro ? (novoPagamentoTroco >= 0 ? novoPagamentoTroco : 0) : undefined,
            ...dadosConciliacao,
            origemDadosPagamento: conciliacaoAlterada ? 'manual' : original?.origemDadosPagamento,
        };

        const updated = pagamentoEditandoIndex !== null
            ? pagamentosLocal.map((p, i) => i === pagamentoEditandoIndex ? { ...p, ...pagamento } : p)
            : [...pagamentosLocal, { ...pagamento, dataHora: new Date().toISOString() }];

        setPagamentosLocal(updated);
        salvarPagamentos(updated);
        handleFecharModalPagamento();
    };

    const handleSelecionarMaquininha = (uuid: string) => {
        if (uuid === MAQUININHA_INTEGRACAO) return;
        const maquininha = maquininhas.find(item => item.uuid === uuid);
        setNovoPagamento(prev => ({ ...prev, maquininhaUuid: uuid || undefined, maquininhaNome: maquininha?.nome }));
    };

    const handleSelecionarBandeira = (uuid: string) => {
        const bandeira = bandeiras.find(item => item.uuid === uuid);
        setNovoPagamento(prev => ({ ...prev, bandeiraUuid: uuid || undefined, bandeiraNome: bandeira?.nome }));
    };

    const { real } = useMoeda();
    const { dataHoraBr, horaBr } = useDataHora();
    const { downloadPorUrl } = useDownload();

    useEffect(() => {
        handleBuscarPedido({ redirecionarSeNaoEncontrado: true });
    }, [uuid]);

    useEffect(() => {
        if (ultimoPedidoAtualizado?.uuid === uuid) {
            handleBuscarPedido();
        }
    }, [ultimoPedidoAtualizado]);

    /**
     * A NF-e é assíncrona: o anexo só fica disponível quando a SEFAZ autoriza, e o aviso chega
     * por webhook. Este intervalo é a rede de segurança para quem já está com a tela aberta.
     *
     * Só roda enquanto existe anexo em processamento, e para sozinho quando a nota resolve,
     * então não há requisição em tela sem nada pendente.
     */
    const aguardandoAnexo = useMemo(
        () => pedido?.anexos?.some(anexo => anexo.status === StatusAnexoPedido.processando) ?? false,
        [pedido?.anexos]
    );

    useEffect(() => {
        if (!aguardandoAnexo) {
            return;
        }

        const intervalo = setInterval(() => handleBuscarPedido(), INTERVALO_ATUALIZACAO_ANEXO_MS);

        return () => clearInterval(intervalo);
    }, [aguardandoAnexo]);

    const handleEmitirNotaFiscal = async (dados: IDadosEmitirNotaFiscal) => {
        if (loading) {
            return;
        }

        if (!pedido) {
            return;
        }

        await emitirNotaFiscal(pedido.uuid, dados);
        snackbar("Nota fiscal enviada para fila de impressão", { severity: "success" });
        fecharModalNotaFiscal();
        setTimeout(() => {
            handleBuscarPedido();
        }, 1000);
    }

    const handleEmitirNFe = async (dados: IDadosEmitirNFe) => {
        if (loading || !pedido) {
            return;
        }

        if (!await emitirNFe(pedido.uuid, dados)) {
            return;
        }

        // A NF-e é assíncrona: neste ponto ela só foi solicitada. A autorização chega pelo
        // webhook da SEFAZ, e é aí que o anexo é liberado e a impressão disparada.
        snackbar("Emissão da NF-e solicitada. A nota será liberada assim que a SEFAZ autorizar.", { severity: "info" });
        fecharModalNFe();
        setTimeout(() => {
            handleBuscarPedido();
        }, 1000);
    }

    const handleImprimirGerarAnexo = async (configuracaoComanda: string) => {
        if (loading) {
            return;
        }

        const configuracaoComandaEncontrada = fluxoContexto
            ?.configuracoesComandas
            ?.find(config => config.uuid == configuracaoComanda);

        if (!configuracaoComandaEncontrada || !pedido) {
            return snackbar("Nenhuma configuração de comanda selecionada", { severity: "error" });
        }

        if (configuracaoComandaEncontrada.tipo == TipoAnexoPedido.nota_fiscal) {
            return abrirModalNotaFiscal();
        }

        await gerarAnexo(pedido.uuid, configuracaoComandaEncontrada.tipo, configuracaoComandaEncontrada.uuid, pedido.produtos);
        snackbar("Comanda enviada para fila de impressão", { severity: "success" });
        fecharModalImpressao();
        setTimeout(() => {
            handleBuscarPedido();
        }, 1000);
    }

    const componentesEndereco = [
        `${pedido?.endereco?.rua} ${pedido?.endereco?.numero}`,
        pedido?.endereco?.bairro,
        pedido?.endereco?.complemento,
        pedido?.endereco?.cep,
        pedido?.endereco?.cidade,
        pedido?.endereco?.regiao?.descricao,
        pedido?.endereco?.referencia,
        pedido?.endereco?.observacao,
    ];
    const componentesEnderecosValidos = componentesEndereco.filter(value => value != undefined && value.length > 0);
    const endereco = componentesEnderecosValidos.join(', ')
    const enderecoCodificado = encodeURIComponent(endereco);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${enderecoCodificado}`;
    const { marketplaces } = useFetchMarketplaces();
    const marketplace = useMemo(() => {
        let marketplace: undefined | IMarketplace;

        marketplace = marketplaces?.find(marketplaceProcurado => marketplaceProcurado.uuid === pedido?.marketplace?.uuid);

        return marketplace
    }, [pedido, marketplaces])

    // O backend retorna valorProdutos e valorFrete SEM desconto, apenas valorTotal vem com desconto
    // Calcular valorTotalProdutos a partir dos produtos para garantir que está correto
    const valorTotalProdutosCalculado = useMemo(() => {
        if (!pedido?.produtos || pedido.produtos.length === 0) {
            return pedido?.valorProdutos || 0;
        }
        let totalProdutos = 0;
        pedido.produtos.forEach(produto => {
            let totalProduto = (produto.preco ?? 0);
            produto.itens?.forEach(item => {
                totalProduto = totalProduto + ((item.preco ?? 0) * (item.quantidade ?? 1));
            });
            totalProdutos = totalProdutos + (totalProduto * (produto.quantidade ?? 1));
        });
        return totalProdutos;
    }, [pedido]);

    // Calcular valorDescontoTotal usando a função de cálculo (mesma lógica do backend)
    const valoresCalculados = useMemo(() => {
        if (!pedido?.cupons || pedido.cupons.length === 0) {
            return {
                valorTotalProdutos: valorTotalProdutosCalculado,
                valorFrete: pedido?.valorFrete || 0,
                valorTotalPedido: valorTotalProdutosCalculado + (pedido?.valorFrete || 0),
                valorDescontoTotal: 0,
            };
        }

        return calcularValoresComCupons(
            {
                valorTotalProdutos: valorTotalProdutosCalculado,
                valorFrete: pedido?.valorFrete || 0,
            },
            pedido.cupons
        );
    }, [valorTotalProdutosCalculado, pedido?.valorFrete, pedido?.cupons]);

    const valorTotalCupons = useMemo(() => {
        return valoresCalculados.valorDescontoTotal;
    }, [valoresCalculados]);

    const valorTotalSemDesconto = useMemo(() => {
        return valorTotalProdutosCalculado + (pedido?.valorFrete || 0);
    }, [valorTotalProdutosCalculado, pedido?.valorFrete])

    // Agrupa os produtos por combo (comboUuid), para exibir o que foi pedido em cada combo.
    const gruposProdutos = useMemo<GrupoProdutoPedido[]>(() => {
        const result: GrupoProdutoPedido[] = [];
        const seen = new Set<string>();
        (pedido?.produtos ?? []).forEach(produto => {
            if (produto.comboUuid) {
                if (!seen.has(produto.comboUuid)) {
                    seen.add(produto.comboUuid);
                    result.push({
                        type: 'combo',
                        comboUuid: produto.comboUuid,
                        comboDescricao: produto.comboDescricao,
                        items: (pedido?.produtos ?? []).filter(p => p.comboUuid === produto.comboUuid),
                    });
                }
            } else {
                result.push({ type: 'single', produto });
            }
        });
        return result;
    }, [pedido]);

    const metadadosCabecalho = useMemo(() => {
        if (!pedido) {
            return [];
        }

        const linhas = [
            pedido.tipo,
            pedido.numeroPedidoDiario ? `Nº diário #${pedido.numeroPedidoDiario}` : undefined,
            pedido.tipo === TipoPedido.mesa && pedido.mesaId ? `Mesa ${pedido.mesaId}` : undefined,
            pedido.dataHora ? `Emitido às ${dataHoraBr(pedido.dataHora as string)}` : undefined,
        ];

        if (pedido.previsaoMinimaEntrega || pedido.previsaoMaximaEntrega) {
            const rotulo = pedido.tipo === TipoPedido.retirada ? "retirada" : "entrega";
            const inicio = pedido.previsaoMinimaEntrega ? horaBr(pedido.previsaoMinimaEntrega as string) : "-";
            const fim = pedido.previsaoMaximaEntrega ? horaBr(pedido.previsaoMaximaEntrega as string) : "-";
            linhas.push(`Previsão de ${rotulo} ${inicio} - ${fim}`);
        }

        return linhas.filter(Boolean) as string[];
    }, [pedido]);

    const podeEditar = pedido
        && pedido.status != StatusPedido.Cancelado
        && (pedido.marketplace?.tipo == TipoMarketplace.PDV || pedido.marketplace?.tipo == TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA);

    const ehDelivery = pedido?.tipo == TipoPedido.delivery;

    if (!pedido) {
        return (
            <Container ref={refContainer}>
                <Skeleton variant="text" width={220} height={44} />
                <Skeleton variant="text" width={320} height={22} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={96} sx={{ mb: 2, borderRadius: "14px" }} />
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: "14px" }} />
            </Container>
        );
    }

    return (
        <Container ref={refContainer}>
            <CabecalhoPedido
                pedido={pedido}
                origem={marketplace?.nome ?? TipoMarketplace.PDV}
                metadados={metadadosCabecalho}
                compacto={compacto}
                acoes={
                    <>
                        <AtualizarStatusPedido
                            pedido={pedido}
                            onStatusAtualizado={() => {
                                handleBuscarPedido();
                                onStatusAtualizado?.();
                            }}
                            zIndex={dialogZIndex}
                        />
                        {/* Imprimir e emitir NF-e saíram do rodapé de anexos: são ações do
                            pedido, e ali quase ninguém as encontrava. */}
                        <Tooltip title="Emitir NF-e">
                            <Button variant="outlined" size="small" startIcon={<ReceiptLong />} onClick={abrirModalNFe}>
                                NF-e
                            </Button>
                        </Tooltip>
                        <Tooltip title="Imprimir comanda">
                            <Button variant="outlined" size="small" startIcon={<Print />} onClick={abrirModalImpressao}>
                                Imprimir
                            </Button>
                        </Tooltip>
                        {podeEditar && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => navigate(`/pedidos/operacao/edicao/${pedido.uuid}`)}
                            >
                                Editar
                            </Button>
                        )}
                    </>
                }
            />

            {pedido.agendadoPara && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    <AlertTitle>Pedido agendado</AlertTitle>
                    Este pedido foi agendado: {pedido.agendadoPara}
                </Alert>
            )}

            <Box sx={{ mt: 2.5 }}>
                <ResumoFinanceiro
                    valorTotal={pedido.valorTotal ?? 0}
                    pagamentos={pagamentosLocal}
                    compacto={compacto}
                />
            </Box>

            <Box
                sx={{
                    mt: 2,
                    display: "grid",
                    // Produtos recebe a coluna maior por ser a seção mais consultada.
                    gridTemplateColumns: compacto ? "1fr" : "1.35fr 1fr",
                    gap: 2,
                    alignItems: "start",
                }}
            >
                <SecaoCartao
                    titulo="Produtos"
                    icone={<Restaurant />}
                    vazio={!gruposProdutos.length}
                    mensagemVazia="Nenhum produto neste pedido"
                >
                    <SecaoProdutos grupos={gruposProdutos} />
                </SecaoCartao>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <SecaoCartao titulo="Valores" icone={<Receipt />} cor={CORES_SECAO.valores}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <LinhaValor rotulo="Produtos" valor={real(valorTotalProdutosCalculado)} />
                            {ehDelivery && <LinhaValor rotulo="Taxa de entrega" valor={real(pedido.valorFrete || 0)} />}
                            {!!pedido.valorTaxaServico && pedido.valorTaxaServico > 0 && (
                                <LinhaValor rotulo="Taxa de serviço" valor={real(pedido.valorTaxaServico)} />
                            )}
                            {!!pedido.cupons?.length && (
                                <>
                                    <LinhaValor rotulo="Subtotal" valor={real(valorTotalSemDesconto)} />
                                    <LinhaValor rotulo="Descontos" valor={`- ${real(valorTotalCupons)}`} destaque="success.main" />
                                </>
                            )}
                            <LinhaValor rotulo="Total" valor={real(pedido.valorTotal)} forte />
                        </Box>
                    </SecaoCartao>

                    <SecaoCartao titulo="Cliente" icone={<Person />} cor={CORES_SECAO.cliente}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.75 }}>
                            <ParDado rotulo="Nome" valor={pedido.cliente?.nome} />
                            <ParDado rotulo="Telefone" valor={pedido.cliente?.telefone} />
                            <ParDado rotulo="CPF/CNPJ" valor={pedido.cliente?.cpf} />
                            <ParDado rotulo="Operador" valor={pedido.operador?.nome} />
                            {!!pedido.cliente?.observacao && (
                                <ParDado rotulo="Observação" valor={pedido.cliente.observacao} largo />
                            )}
                        </Box>
                    </SecaoCartao>

                    {/* Entrega só aparece em delivery: nos demais tipos era um cartão inteiro
                        ocupado por um aviso de que não havia endereço. */}
                    {ehDelivery && (
                        <SecaoCartao titulo="Entrega" icone={<LocalShipping />} cor={CORES_SECAO.entrega}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.75 }}>
                                {!!pedido.codigoColeta && <ParDado rotulo="Código de coleta" valor={pedido.codigoColeta} />}
                                <ParDado rotulo="Endereço" valor={endereco} />
                                {!!pedido.entregador && (
                                    <ParDado
                                        rotulo="Entregador"
                                        valor={`${pedido.entregador.nome}${pedido.entregador.telefone ? ` · ${pedido.entregador.telefone}` : ""}`}
                                    />
                                )}
                                <Link href={googleMapsUrl} target="_blank" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: "0.875rem" }}>
                                    <Place fontSize="small" /> Abrir no mapa
                                </Link>
                            </Box>
                        </SecaoCartao>
                    )}
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <SecaoCartao
                    titulo="Pagamentos"
                    icone={<CreditCard />}
                    cor={CORES_SECAO.pagamentos}
                    acoes={
                        <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAbrirNovoPagamento}>
                            Adicionar
                        </Button>
                    }
                    vazio={!pagamentosLocal.length}
                    mensagemVazia="Nenhum pagamento registrado"
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: compacto ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 1.5,
                        }}
                    >
                        {pagamentosLocal.map((pagamento, index) => (
                            <CartaoPagamento
                                key={index}
                                pagamento={pagamento}
                                onEditar={() => handleAbrirEdicaoPagamento(index)}
                                onRemover={() => handleRemoverPagamento(index)}
                                onAlternarPago={pago => handleSetPago(index, pago)}
                            />
                        ))}
                    </Box>
                </SecaoCartao>
            </Box>

            {/* Cupons só entram quando existem: antes a seção aparecia sempre, com uma tabela
                de cinco colunas dizendo que nenhum cupom foi usado. */}
            {!!pedido.cupons?.length && (
                <Box sx={{ mt: 2 }}>
                    <SecaoCartao titulo="Cupons" icone={<ConfirmationNumber />} cor={CORES_SECAO.cupons} semRecuo>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <CelulaCabecalho>Código</CelulaCabecalho>
                                        <CelulaCabecalho>Tipo</CelulaCabecalho>
                                        <CelulaCabecalho align="right">Valor</CelulaCabecalho>
                                        <CelulaCabecalho align="right">Refletido</CelulaCabecalho>
                                        <CelulaCabecalho align="center">Patrocinadores</CelulaCabecalho>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pedido.cupons.map((cupom, index) => {
                                        // Calcular o desconto deste cupom considerando os cupons aplicados antes dele
                                        const cuponsAnteriores = pedido?.cupons?.slice(0, index) || [];
                                        const valorRefletido = calcularDescontoCupomComCuponsSelecionados(
                                            cupom,
                                            valorTotalProdutosCalculado,
                                            pedido?.valorFrete || 0,
                                            cuponsAnteriores
                                        );

                                        const isPorcentagem = cupom.tipo === TipoCupom.PORCENTAGEM || cupom.tipo === TipoCupom.PORCENTAGEM_PRODUTOS;
                                        const valorFormatado = isPorcentagem ? `${cupom.valor}%` : real(cupom.valor);
                                        const temPatrocinadores = cupom.patrocinadores && cupom.patrocinadores.length > 0;

                                        return (
                                            <LinhaTabela key={index}>
                                                <CelulaCorpo>{cupom.codigo || "—"}</CelulaCorpo>
                                                <CelulaCorpo>{TipoCupomDescricao[cupom.tipo]}</CelulaCorpo>
                                                <CelulaCorpo align="right">{valorFormatado}</CelulaCorpo>
                                                <CelulaCorpo align="right">{real(valorRefletido)}</CelulaCorpo>
                                                <CelulaCorpo align="center">
                                                    {temPatrocinadores ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => abrirModalPatrocinadores(cupom.patrocinadores || [])}
                                                            color="primary"
                                                        >
                                                            <Info fontSize="small" />
                                                        </IconButton>
                                                    ) : "—"}
                                                </CelulaCorpo>
                                            </LinhaTabela>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SecaoCartao>
                </Box>
            )}

            <Box
                sx={{
                    mt: 2,
                    display: "grid",
                    gridTemplateColumns: compacto ? "1fr" : "1fr 1fr",
                    gap: 2,
                    alignItems: "start",
                }}
            >
                <SecaoCartao
                    titulo="Anexos"
                    icone={<AttachFile />}
                    cor={CORES_SECAO.anexos}
                    semRecuo={!!pedido.anexos?.length}
                    vazio={!pedido.anexos?.length}
                    mensagemVazia="Nenhum anexo gerado"
                >
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <CelulaCabecalho>Descrição</CelulaCabecalho>
                                    <CelulaCabecalho>Tipo</CelulaCabecalho>
                                    <CelulaCabecalho>Data/Hora</CelulaCabecalho>
                                    <CelulaCabecalho align="center">Ações</CelulaCabecalho>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pedido.anexos?.map(anexo => {
                                    // Anexo sem arquivo no storage (NF-e aguardando a SEFAZ, ou
                                    // rejeitada) não tem o que baixar nem abrir. Anexos antigos
                                    // não trazem status, e nesses a url é o que vale.
                                    const disponivel = anexo.status
                                        ? anexo.status === StatusAnexoPedido.disponivel
                                        : !!anexo.url;

                                    return (
                                        <LinhaTabela key={anexo.uuid}>
                                            <CelulaCorpo>{anexo.descricao}</CelulaCorpo>
                                            <CelulaCorpo>{TipoAnexoDescricao[anexo.tipo]}</CelulaCorpo>
                                            <CelulaCorpo>{dataHoraBr(anexo.dataHora)}</CelulaCorpo>
                                            <CelulaCorpo align="center">
                                                {disponivel ? (
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.25 }}>
                                                        <Tooltip title="Baixar">
                                                            <IconButton size="small" onClick={() => downloadPorUrl(anexo.url, anexo.nome)}>
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Abrir em nova aba">
                                                            <IconButton size="small" onClick={() => window.open(anexo.url, '_blank')}>
                                                                <OpenInNew fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Chip size="small" variant="outlined" label="Processando" sx={{ height: 22, fontSize: "0.7rem" }} />
                                                )}
                                            </CelulaCorpo>
                                        </LinhaTabela>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </SecaoCartao>

                <SecaoCartao
                    titulo="Linha do tempo"
                    icone={<History />}
                    cor={CORES_SECAO.timeline}
                    vazio={!pedido.historicoStatus?.length}
                    mensagemVazia="Sem histórico de status"
                >
                    {/* A timeline é vertical e cresce com o histórico; o limite de altura evita
                        que um pedido com muitas transições estique a página inteira. */}
                    <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                        <HistoricoPedidoTimeline dataHoraEmissao={pedido.dataHora} historico={pedido.historicoStatus ?? []} />
                    </Box>
                </SecaoCartao>

                <SecaoCartao
                    titulo="Avaliação"
                    icone={<StarRounded />}
                    cor={CORES_SECAO.avaliacao}
                    vazio={!pedido.avaliacao}
                    mensagemVazia="Nenhuma avaliação solicitada para este pedido"
                >
                    <AvaliacaoPedidoDetalhe avaliacao={pedido.avaliacao} />
                </SecaoCartao>
            </Box>

            <Dialog
                open={modalNovoPagamentoOpen}
                onClose={handleFecharModalPagamento}
                maxWidth="xs"
                fullWidth
                style={{ zIndex: 9999 }}
            >
                <DialogTitle>{pagamentoEditandoIndex !== null ? "Editar pagamento" : "Adicionar pagamento"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Valor"
                            fullWidth
                            value={novoPagamento.valor || ''}
                            onChange={(e) => setNovoPagamento(prev => ({ ...prev, valor: parseFloat(e.target.value) || 0 }))}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Método de pagamento</InputLabel>
                            <Select
                                label="Método de pagamento"
                                value={novoPagamento.metodoPagamento}
                                onChange={(e) => setNovoPagamento(prev => ({ ...prev, metodoPagamento: e.target.value }))}
                                MenuProps={{ sx: { zIndex: 10000 } }}
                            >
                                {configuracao?.metodosPagamento?.map(metodo => (
                                    <MenuItem key={metodo} value={metodo}>{metodo}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {novoPagamento.metodoPagamento === MetodoPagamento.Dinheiro && (
                            <>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Valor recebido"
                                    fullWidth
                                    value={novoPagamento.valorRecebido ?? ''}
                                    onChange={(e) => setNovoPagamento(prev => ({ ...prev, valorRecebido: parseFloat(e.target.value) || 0 }))}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                                <Alert severity={novoPagamentoTroco >= 0 ? "success" : "error"}>
                                    Troco: {real(novoPagamentoTroco >= 0 ? novoPagamentoTroco : 0)}
                                </Alert>
                            </>
                        )}
                        {/* Dados de conciliação: opcionais, e sem sentido para dinheiro. */}
                        {novoPagamento.metodoPagamento && novoPagamento.metodoPagamento !== MetodoPagamento.Dinheiro && (
                            <>
                                <Typography variant="caption" color="text.secondary">
                                    Dados para conciliação (opcionais)
                                </Typography>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Maquininha/Instituição</InputLabel>
                                    <Select
                                        label="Maquininha/Instituição"
                                        value={novoPagamento.maquininhaUuid || (novoPagamento.maquininhaNome ? MAQUININHA_INTEGRACAO : '')}
                                        onChange={(e) => handleSelecionarMaquininha(e.target.value)}
                                        MenuProps={{ sx: { zIndex: 10000 } }}
                                    >
                                        <MenuItem value=""><em>Não informado</em></MenuItem>
                                        {/* Instituição vinda da integração (ex.: Woovi), que não é uma maquininha cadastrada. */}
                                        {!novoPagamento.maquininhaUuid && novoPagamento.maquininhaNome && (
                                            <MenuItem value={MAQUININHA_INTEGRACAO}>
                                                {novoPagamento.maquininhaNome} (integração)
                                            </MenuItem>
                                        )}
                                        {maquininhas.map(maquininha => (
                                            <MenuItem key={maquininha.uuid} value={maquininha.uuid}>{maquininha.nome}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Tipo</InputLabel>
                                    <Select
                                        label="Tipo"
                                        value={novoPagamento.tipoCartao ?? ''}
                                        onChange={(e) => setNovoPagamento(prev => ({ ...prev, tipoCartao: e.target.value || undefined }))}
                                        MenuProps={{ sx: { zIndex: 10000 } }}
                                    >
                                        <MenuItem value=""><em>Não informado</em></MenuItem>
                                        {Object.values(TipoCartao).map(tipo => (
                                            <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Bandeira</InputLabel>
                                    <Select
                                        label="Bandeira"
                                        value={novoPagamento.bandeiraUuid ?? ''}
                                        onChange={(e) => handleSelecionarBandeira(e.target.value)}
                                        MenuProps={{ sx: { zIndex: 10000 } }}
                                    >
                                        <MenuItem value=""><em>Não informado</em></MenuItem>
                                        {bandeiras.map(bandeira => (
                                            <MenuItem key={bandeira.uuid} value={bandeira.uuid}>{bandeira.nome}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Nº de autorização"
                                    fullWidth
                                    value={novoPagamento.numeroAutorizacao ?? ''}
                                    onChange={(e) => setNovoPagamento(prev => ({ ...prev, numeroAutorizacao: e.target.value }))}
                                    inputProps={{ maxLength: 60 }}
                                />
                            </>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch
                                checked={novoPagamento.pago}
                                onChange={(e) => setNovoPagamento(prev => ({ ...prev, pago: e.target.checked }))}
                                color="success"
                            />
                            <Typography variant="body2" color={novoPagamento.pago ? "success.main" : "text.secondary"}>
                                {novoPagamento.pago ? "Pago" : "Pendente"}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFecharModalPagamento}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSalvarPagamento}
                        disabled={!novoPagamento.metodoPagamento || novoPagamento.valor <= 0}
                    >
                        {pagamentoEditandoIndex !== null ? "Salvar" : "Adicionar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {fluxoContexto && (
                <ImprimirModal
                    fluxoContexto={fluxoContexto}
                    fechar={fecharModalImpressao}
                    imprimir={handleImprimirGerarAnexo}
                    loading={loadingAnexo}
                    open={imprimirOpen}
                />
            )}
            <EmitirNotaFiscalModal
                cancelar={fecharModalNotaFiscal}
                concluir={handleEmitirNotaFiscal}
                open={notaFiscalOpen}
                loading={loadingAnexo}
            />
            <EmitirNFeModal
                cancelar={fecharModalNFe}
                concluir={handleEmitirNFe}
                open={nfeOpen}
                loading={loadingAnexo}
                clienteUuid={pedido.cliente?.uuid}
            />
            <PatrocinadoresCupomModal
                open={patrocinadoresModalOpen}
                onClose={fecharModalPatrocinadores}
                patrocinadores={patrocinadoresSelecionados}
            />
        </Container>
    );
}

/** Linha do detalhamento de valores: rótulo à esquerda, número alinhado à direita. */
const LinhaValor = ({
    rotulo,
    valor,
    forte,
    destaque,
}: {
    rotulo: string;
    valor: string;
    forte?: boolean;
    destaque?: string;
}) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 2,
            pt: forte ? 1 : 0,
            borderTop: theme => (forte ? `1px solid ${theme.palette.divider}` : "none"),
        }}
    >
        <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{rotulo}</Typography>
        <Typography
            sx={{
                fontSize: forte ? "1rem" : "0.875rem",
                fontWeight: forte ? 700 : 500,
                whiteSpace: "nowrap",
                color: destaque ?? "text.primary",
            }}
        >
            {valor}
        </Typography>
    </Box>
);
